import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ListingResponse } from "../../../api/types/Listing";
import { TLegacyVehicle } from "../../../api/types/Vehicle";
import Tooltip from "../../../components/cameraAdmin/tooltip";
import {
  APP_CONFIG,
  REPORT_FILTER_BY_OPTION_FIELD_NAME,
  REPORT_FILTER_BY_OPTION_VALUES,
} from "../../../constants/constants";
import { useLoggedInUserData } from "../../../utils/user";
import { consistentDisplayAssetItem } from "../../../utils/vehicle";
import SelectedOptionValuesDropdown from "./selectedOptionValuesDropdown";

const ReportHeaderFiltersDisplayLabel = ({
  paramJson,
  vehiclesList,
}: {
  paramJson: any;
  vehiclesList?: ListingResponse<TLegacyVehicle[]>;
}) => {
  const { t: tReports } = useTranslation("translation", {
    keyPrefix: "reports",
  });
  const { t: tReportsOptionsForm } = useTranslation("translation", {
    keyPrefix: "reports.optionsForm",
  });

  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";

  const [showOptionValuesDrop, setShowOptionValuesDrop] = useState(false);

  return (
    <div className="text-sm leading-4 mt-1 flex gap-3">
      {Object.keys(APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS).map(
        (filter, index) => {
          const field_config =
            APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS[
              filter as keyof typeof APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
            ];
          const isMulti = field_config.multiple;
          const showAllOption = field_config.faux_option_all;
          const field_name = field_config.field_name;
          if (paramJson[field_name] === undefined) return null;
          const paramValues = (
            typeof paramJson[field_name] === "object"
              ? paramJson[field_name]?.map((item: any) => item.value)
              : paramJson[field_name]?.split(",") || []
          )?.filter((item: string) => !!item || !!field_config.faux_option_all);
          // hide if no values and no showAllOption
          if (paramValues.length === 0 && !showAllOption) return null;
          // hide if filter_by is active
          if (!!paramJson["filter_by"]) {
            const filterByKey = Object.keys(
              REPORT_FILTER_BY_OPTION_VALUES
            ).find(
              (key) =>
                REPORT_FILTER_BY_OPTION_VALUES[
                  key as keyof typeof REPORT_FILTER_BY_OPTION_VALUES
                ] === paramJson["filter_by"]
            );
            const filterByFieldName =
              REPORT_FILTER_BY_OPTION_FIELD_NAME[
                filterByKey as keyof typeof REPORT_FILTER_BY_OPTION_VALUES
              ];
            if (filterByFieldName !== field_name) return null;
          }

          const displayLabelValueAsAllEntities =
            paramJson[`${field_name}_all`] ||
            (!!field_config.faux_option_all &&
              (paramJson[field_name] === "" ||
                [
                  APP_CONFIG.REPORTS.GROUP_ASSET_ALL_VALUE,
                  APP_CONFIG.REPORTS.GROUP_ASSET_ALL_ACTIVE_VALUE,
                ].includes(paramJson[field_name]?.[0]?.value)));

          const displayAllAssetsFilter =
            !!displayLabelValueAsAllEntities &&
            field_name ===
              APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name
              ? paramJson[`${field_name}_all_filter`] === true ||
                paramJson[`${field_name}`]?.[0]?.value ===
                  APP_CONFIG.REPORTS.GROUP_ASSET_ALL_ACTIVE_VALUE
                ? tReportsOptionsForm(
                    `${field_name}_all_filter_on_descr`
                  ).toLowerCase()
                : tReportsOptionsForm(
                    `${field_name}_all_filter_off_descr`
                  ).toLowerCase()
              : !!displayLabelValueAsAllEntities &&
                  field_name ===
                    APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GROUP.field_name
                ? paramJson[
                    APP_CONFIG.REPORTS.GROUP_ASSETS_FILTER.field_name
                  ] === "1"
                  ? tReportsOptionsForm(
                      `${field_name}_all_filter_on_descr`
                    ).toLowerCase()
                  : tReportsOptionsForm(
                      `${field_name}_all_filter_off_descr`
                    ).toLowerCase()
                : null;

          const displayAllOrActiveAssetsLabelWithSpecificGroups =
            !displayLabelValueAsAllEntities && // only specific
            field_name ===
              APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GROUP.field_name &&
            paramJson[field_name].length > 0;

          const allOrActiveLabelForSpecificGroups =
            !!displayAllOrActiveAssetsLabelWithSpecificGroups
              ? paramJson[APP_CONFIG.REPORTS.GROUP_ASSETS_FILTER.field_name] ===
                "1"
                ? tReportsOptionsForm(
                    `${field_name}_all_filter_on_descr`
                  ).toLowerCase()
                : tReportsOptionsForm(
                    `${field_name}_all_filter_off_descr`
                  ).toLowerCase()
              : null;

          const displayIndividualValues =
            paramValues.length > 0 &&
            ![
              APP_CONFIG.REPORTS.GROUP_ASSET_ALL_VALUE,
              APP_CONFIG.REPORTS.GROUP_ASSET_ALL_ACTIVE_VALUE,
            ].includes(paramValues?.[0]);

          const displayLabel =
            displayLabelValueAsAllEntities || displayIndividualValues;

          if (!displayLabel) return null;

          return (
            <div className="flex items-center" key={index}>
              {/* Label */}
              <div key={filter} className="font-semibold mr-1">
                {tReports(`label_${field_name}`)}:
              </div>

              {/* Label value for All Groups/Assets/etc. */}
              {displayLabelValueAsAllEntities && (
                <>
                  {tReportsOptionsForm(`${field_name}_all`)}
                  {!!displayAllAssetsFilter
                    ? ` (${displayAllAssetsFilter})`
                    : ""}
                </>
              )}

              {/* Display first 3 values */}
              {displayIndividualValues && (
                <div className="flex items-center ">
                  {paramValues.slice(0, 3).map((val: string, i: number) => {
                    let tooltipText = val;
                    // find the vehicle in the fetched list
                    if (
                      field_name ===
                        APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE
                          .field_name &&
                      vehiclesList?.results
                    ) {
                      const getVehicle = vehiclesList.results.find(
                        (v: any) => String(v.deviceid) === String(val)
                      );
                      if (getVehicle) {
                        tooltipText = consistentDisplayAssetItem(
                          getVehicle.deviceid,
                          getVehicle.description,
                          getVehicle.displayname,
                          getVehicle.drivername,
                          showDriver
                        );
                      }
                    }
                    // Fallback
                    else {
                      const originalItem = Array.isArray(paramJson[field_name])
                        ? paramJson[field_name].find(
                            (p: any) => p.value === val
                          )
                        : null;
                      if (originalItem?.label) tooltipText = originalItem.label;
                    }
                    return (
                      <div key={i} className="flex items-center">
                        <span>{val}</span>
                        <span className="mt-0.5 me-3 -ms-1">
                          <Tooltip description={tooltipText} />
                          {""}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Display +n others label */}
              {paramValues.length > 3 && (
                <div className="relative ml-1">
                  <button
                    type="button"
                    className="font-semibold"
                    onClick={() =>
                      setShowOptionValuesDrop(!showOptionValuesDrop)
                    }
                  >
                    {`${tReports(
                      paramValues.length - 3 === 1
                        ? "plus_n_others_1"
                        : "plus_n_others",
                      { n: paramValues.length - 3 }
                    )}`}
                  </button>
                  {showOptionValuesDrop && (
                    <>
                      <div
                        className="fixed top-0 left-0 w-full h-screen bg-white/70 backdrop-blur-[1px] z-popup-overlay"
                        onClick={() =>
                          setShowOptionValuesDrop(!showOptionValuesDrop)
                        }
                      ></div>
                      <SelectedOptionValuesDropdown
                        title={tReports(`label_${field_name}`)}
                        values={paramValues.map((val: string) => {
                          if (
                            field_name ===
                              APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE
                                .field_name &&
                            vehiclesList?.results
                          ) {
                            const getVehicle = vehiclesList.results.find(
                              (v: any) => String(v.deviceid) === String(val)
                            );
                            if (getVehicle) {
                              return consistentDisplayAssetItem(
                                getVehicle.deviceid,
                                getVehicle.description,
                                getVehicle.displayname,
                                getVehicle.drivername,
                                showDriver
                              );
                            }
                          }
                          // Fallback for other fields (like Groups) or if Asset not found yet
                          const originalItem = Array.isArray(
                            paramJson[field_name]
                          )
                            ? paramJson[field_name].find(
                                (p: any) => p.value === val
                              )
                            : null;

                          return originalItem?.label || val;
                        })}
                      />
                    </>
                  )}
                </div>
              )}

              {/* Label value for All Groups/Assets/etc. */}
              {displayAllOrActiveAssetsLabelWithSpecificGroups && (
                <>
                  {!!allOrActiveLabelForSpecificGroups
                    ? ` (${allOrActiveLabelForSpecificGroups})`
                    : ""}
                </>
              )}
            </div>
          );
        }
      )}
    </div>
  );
};
export default ReportHeaderFiltersDisplayLabel;
